import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useAuth, useOrganization } from '@clerk/clerk-react'

import { evalKeys } from '@/lib/queryKeys'
import type { EvalCaseAssertion, EvalCaseAssertionSeverity } from '../types'
import {
  createEvalAssertion,
  deleteEvalAssertion,
  fetchEvalCaseAssertions,
  updateEvalAssertion,
} from '../api'

const NO_AUTH_TOKEN_ERROR = 'No authentication token available'

export type DraftAssertion = {
  id?: string
  name: string
  criteria: string
  severity: EvalCaseAssertionSeverity
}

export type SaveAssertionsInput = {
  draft: DraftAssertion[]
  server: EvalCaseAssertion[]
}

// Diff the draft rows against the server rows and apply changes SEQUENTIALLY —
// reactivating a soft-deleted row by name avoids the unique(name) violation a
// create would hit, and awaiting each step avoids transient collisions when a
// rename frees up a name another row wants.
async function saveAssertions(
  token: string,
  caseId: string,
  { draft, server }: SaveAssertionsInput
): Promise<void> {
  const all = await fetchEvalCaseAssertions(token, caseId, {
    includeInactive: true,
  })
  const inactiveByName = new Map<string, EvalCaseAssertion>()
  for (const row of all.items) {
    if (!row.active) inactiveByName.set(row.name, row)
  }

  const serverById = new Map(server.map((row) => [row.id, row]))

  for (const [i, row] of draft.entries()) {
    const existing = row.id ? serverById.get(row.id) : undefined

    if (existing) {
      if (
        existing.name !== row.name ||
        existing.criteria !== row.criteria ||
        existing.severity !== row.severity ||
        existing.position !== i
      ) {
        await updateEvalAssertion(token, caseId, existing.id, {
          name: row.name,
          criteria: row.criteria,
          severity: row.severity,
          position: i,
        })
      }
      continue
    }

    const inactive = inactiveByName.get(row.name)
    if (inactive) {
      await updateEvalAssertion(token, caseId, inactive.id, {
        active: true,
        criteria: row.criteria,
        severity: row.severity,
        position: i,
      })
      continue
    }

    await createEvalAssertion(token, caseId, {
      name: row.name,
      criteria: row.criteria,
      severity: row.severity,
      position: i,
    })
  }

  const draftIds = new Set(
    draft.map((row) => row.id).filter((id): id is string => Boolean(id))
  )
  for (const row of server) {
    if (!draftIds.has(row.id)) {
      await deleteEvalAssertion(token, caseId, row.id)
    }
  }
}

export function useSaveAssertionsMutation(caseId: string) {
  const { getToken } = useAuth()
  const { organization } = useOrganization()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (input: SaveAssertionsInput) => {
      const token = await getToken()
      if (!token) throw new Error(NO_AUTH_TOKEN_ERROR)
      await saveAssertions(token, caseId, input)
    },
    // Invalidate on settle (success AND error): a batch save applies per-row
    // ops sequentially, so a mid-sequence failure commits a prefix. Refetching
    // on error too resyncs the card to true server state instead of leaving the
    // user editing stale draft over a half-applied save.
    onSettled: () => {
      void queryClient.invalidateQueries({
        queryKey: evalKeys.caseAssertions(organization?.id, caseId),
      })
    },
  })
}
